// Frontend service for dashboard configuration management
export interface DashboardConfiguration {
  id?: number;
  userId: number;
  organizationId: number;
  name: string;
  description?: string;
  isDefault: boolean;
  isShared: boolean;
  sharePermissions?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
  tabs?: DashboardTab[];
}

export interface DashboardTab {
  id?: number;
  dashboardConfigId?: number;
  tabName: string;
  tabIcon?: string;
  tabDescription?: string;
  tabOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  widgets?: DashboardWidget[];
}

export interface DashboardWidget {
  id?: number;
  tabId?: number;
  widgetName: string;
  widgetType: 'chart' | 'metric_card' | 'table' | 'insight_card' | 'kpi_summary';
  chartType?: 'bar' | 'line' | 'pie' | 'doughnut' | 'scatter' | 'radar' | 'bubble' | 'area' | 'horizontalBar';
  positionX: number;
  positionY: number;
  width: number; // 1-12 (grid system)
  height: number; // 1-12
  widgetConfig: WidgetConfiguration;
  dataConfig: DataConfiguration;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface WidgetConfiguration {
  colorPresetId?: number;
  customColors?: string[];
  showLegend?: boolean;
  showDataLabels?: boolean;
  chartOptions?: Record<string, any>;
  title?: string;
  subtitle?: string;
}

export interface DataConfiguration {
  kpiCodes: string[];
  calculationType: 'raw' | 'network_average' | 'location_comparison' | 'custom';
  calculationId?: number;
  timeRange: 'last_3_months' | 'last_6_months' | 'last_year' | 'custom' | 'current_month' | 'current_year';
  customDateRange?: {
    startDate: string;
    endDate: string;
  };
  locationFilters?: number[];
  aggregationType: 'sum' | 'average' | 'max' | 'min' | 'count';
  groupBy?: 'month' | 'location' | 'kpi' | 'none';
  filters?: Record<string, any>;
}

export interface DashboardOptions {
  availableKpis: KPIOption[];
  availableCalculations: CalculationOption[];
  colorPresets: ColorPreset[];
  accessibleLocations: LocationOption[];
}

export interface KPIOption {
  code: string;
  name: string;
  category: string;
  subcategory?: string;
  units?: string;
  description?: string;
}

export interface CalculationOption {
  id: number;
  name: string;
  description?: string;
  calculationType: string;
  requiredKpis: string[];
  outputFormat: string;
}

export interface ColorPreset {
  id: number;
  name: string;
  description?: string;
  colors: string[];
  isSystemDefault: boolean;
}

export interface LocationOption {
  id: number;
  name: string;
  code?: string;
  type?: string;
}

export interface CreateDashboardRequest {
  name: string;
  description?: string;
  isDefault?: boolean;
  templateId?: number;
  tabs: CreateTabRequest[];
}

export interface CreateTabRequest {
  tabName: string;
  tabIcon?: string;
  tabDescription?: string;
  tabOrder: number;
  widgets: CreateWidgetRequest[];
}

export interface CreateWidgetRequest {
  widgetName: string;
  widgetType: 'chart' | 'metric_card' | 'table' | 'insight_card' | 'kpi_summary';
  chartType?: string;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  widgetConfig: any;
  dataConfig: any;
}

export interface ChartData {
  type: string;
  title: string;
  description?: string;
  labels: string[];
  datasets: ChartDataset[];
  xAxisLabel?: string;
  yAxisLabel?: string;
}

export interface ChartDataset {
  label: string;
  data: number[] | Array<{x: number, y: number}> | Array<{x: number, y: number, r: number}>;
  backgroundColor?: string | string[];
  borderColor?: string | string[];
  borderWidth?: number;
  fill?: boolean;
  tension?: number;
}

class DashboardConfigurationService {
  private baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  /**
   * Get authorization headers
   */
  private getHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Handle API response
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'API request failed');
    }
    
    return data.data;
  }

  /**
   * Get all dashboard configurations for the current user
   */
  async getDashboards(): Promise<DashboardConfiguration[]> {
    try {
      console.log('📊 Fetching dashboard configurations...');
      
      const response = await fetch(`${this.baseURL}/dashboard-configs`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const dashboards = await this.handleResponse<DashboardConfiguration[]>(response);
      console.log(`✅ Fetched ${dashboards.length} dashboard configurations`);
      
      return dashboards;
    } catch (error) {
      console.error('❌ Error fetching dashboards:', error);
      throw error;
    }
  }

  /**
   * Get specific dashboard configuration with full structure
   */
  async getDashboard(dashboardId: number): Promise<DashboardConfiguration> {
    try {
      console.log(`📊 Fetching dashboard configuration ${dashboardId}...`);
      
      const response = await fetch(`${this.baseURL}/dashboard-configs/${dashboardId}`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const dashboard = await this.handleResponse<DashboardConfiguration>(response);
      console.log(`✅ Fetched dashboard: ${dashboard.name}`);
      
      return dashboard;
    } catch (error) {
      console.error(`❌ Error fetching dashboard ${dashboardId}:`, error);
      throw error;
    }
  }

  /**
   * Create new dashboard configuration
   */
  async createDashboard(request: CreateDashboardRequest): Promise<DashboardConfiguration> {
    try {
      console.log('📊 Creating dashboard configuration...', request.name);
      
      const response = await fetch(`${this.baseURL}/dashboard-configs`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(request)
      });

      const dashboard = await this.handleResponse<DashboardConfiguration>(response);
      console.log(`✅ Created dashboard: ${dashboard.name}`);
      
      return dashboard;
    } catch (error) {
      console.error('❌ Error creating dashboard:', error);
      throw error;
    }
  }

  /**
   * Update dashboard configuration
   */
  async updateDashboard(
    dashboardId: number, 
    updates: Partial<DashboardConfiguration>
  ): Promise<DashboardConfiguration> {
    try {
      console.log(`📊 Updating dashboard configuration ${dashboardId}...`);
      
      const response = await fetch(`${this.baseURL}/dashboard-configs/${dashboardId}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(updates)
      });

      const dashboard = await this.handleResponse<DashboardConfiguration>(response);
      console.log(`✅ Updated dashboard: ${dashboard.name}`);
      
      return dashboard;
    } catch (error) {
      console.error(`❌ Error updating dashboard ${dashboardId}:`, error);
      throw error;
    }
  }

  /**
   * Delete dashboard configuration
   */
  async deleteDashboard(dashboardId: number): Promise<void> {
    try {
      console.log(`📊 Deleting dashboard configuration ${dashboardId}...`);
      
      const response = await fetch(`${this.baseURL}/dashboard-configs/${dashboardId}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });

      await this.handleResponse<void>(response);
      console.log(`✅ Deleted dashboard ${dashboardId}`);
    } catch (error) {
      console.error(`❌ Error deleting dashboard ${dashboardId}:`, error);
      throw error;
    }
  }

  /**
   * Set dashboard as default
   */
  async setDefaultDashboard(dashboardId: number): Promise<void> {
    try {
      console.log(`📊 Setting dashboard ${dashboardId} as default...`);
      
      const response = await fetch(`${this.baseURL}/dashboard-configs/${dashboardId}/set-default`, {
        method: 'POST',
        headers: this.getHeaders()
      });

      await this.handleResponse<void>(response);
      console.log(`✅ Set dashboard ${dashboardId} as default`);
    } catch (error) {
      console.error(`❌ Error setting default dashboard ${dashboardId}:`, error);
      throw error;
    }
  }

  /**
   * Get dashboard configuration options (KPIs, calculations, colors, etc.)
   */
  async getDashboardOptions(): Promise<DashboardOptions> {
    try {
      console.log('📊 Fetching dashboard options...');
      console.log('🌐 Request URL:', `${this.baseURL}/dashboard-configs/options/all`);
      console.log('🔑 Headers:', this.getHeaders());
      
      const response = await fetch(`${this.baseURL}/dashboard-configs/options/all`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      console.log('📡 Response status:', response.status, response.statusText);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const options = await this.handleResponse<DashboardOptions>(response);
      console.log('✅ Fetched dashboard options:', {
        kpis: options.availableKpis?.length || 0,
        calculations: options.availableCalculations?.length || 0,
        colorPresets: options.colorPresets?.length || 0,
        locations: options.accessibleLocations?.length || 0
      });
      
      return options;
    } catch (error) {
      console.error('❌ Error fetching dashboard options:', error);
      throw error;
    }
  }

  /**
   * Add widget to dashboard tab
   */
  async addWidget(tabId: number, widget: CreateWidgetRequest): Promise<DashboardWidget> {
    try {
      console.log(`📊 Adding widget to tab ${tabId}...`, widget.widgetName);
      
      const response = await fetch(`${this.baseURL}/dashboard-configs/tabs/${tabId}/widgets`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(widget)
      });

      const createdWidget = await this.handleResponse<DashboardWidget>(response);
      console.log(`✅ Added widget: ${createdWidget.widgetName}`);
      
      return createdWidget;
    } catch (error) {
      console.error(`❌ Error adding widget to tab ${tabId}:`, error);
      throw error;
    }
  }

  /**
   * Update widget configuration
   */
  async updateWidget(
    widgetId: number, 
    updates: Partial<DashboardWidget>
  ): Promise<DashboardWidget> {
    try {
      console.log(`📊 Updating widget ${widgetId}...`);
      
      const response = await fetch(`${this.baseURL}/dashboard-configs/widgets/${widgetId}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(updates)
      });

      const widget = await this.handleResponse<DashboardWidget>(response);
      console.log(`✅ Updated widget: ${widget.widgetName}`);
      
      return widget;
    } catch (error) {
      console.error(`❌ Error updating widget ${widgetId}:`, error);
      throw error;
    }
  }

  /**
   * Delete widget
   */
  async deleteWidget(widgetId: number): Promise<void> {
    try {
      console.log(`📊 Deleting widget ${widgetId}...`);
      
      const response = await fetch(`${this.baseURL}/dashboard-configs/widgets/${widgetId}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });

      await this.handleResponse<void>(response);
      console.log(`✅ Deleted widget ${widgetId}`);
    } catch (error) {
      console.error(`❌ Error deleting widget ${widgetId}:`, error);
      throw error;
    }
  }

  /**
   * Get data for a specific widget
   */
  async getWidgetData(widgetId: number): Promise<ChartData> {
    try {
      console.log(`📊 Fetching data for widget ${widgetId}...`);
      
      const response = await fetch(`${this.baseURL}/dashboard-configs/widgets/${widgetId}/data`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const chartData = await this.handleResponse<ChartData>(response);
      console.log(`✅ Fetched widget data: ${chartData.datasets[0]?.data?.length || 0} data points`);
      
      return chartData;
    } catch (error) {
      console.error(`❌ Error fetching widget data ${widgetId}:`, error);
      throw error;
    }
  }

  /**
   * Create a dashboard from template
   */
  async createFromTemplate(templateId: number, name: string, description?: string): Promise<DashboardConfiguration> {
    try {
      console.log(`📊 Creating dashboard from template ${templateId}...`);
      
      // This would be implemented when templates are fully supported
      const request: CreateDashboardRequest = {
        name,
        description,
        templateId,
        tabs: [{
          tabName: 'Overview',
          tabIcon: 'dashboard',
          tabOrder: 0,
          widgets: []
        }]
      };

      return await this.createDashboard(request);
    } catch (error) {
      console.error(`❌ Error creating dashboard from template ${templateId}:`, error);
      throw error;
    }
  }
}

export default new DashboardConfigurationService();
